// =============================================================================
// AGC: DROID FARM - Field Monitoring Card
// AGC: Main container for satellite-based field health monitoring
// AGC: Includes health score, NDVI chart, stress alerts, and management zones
// =============================================================================

import React, { useState, useCallback, useMemo } from 'react'
import { 
  Satellite, 
  ChevronDown, 
  ChevronUp, 
  RefreshCw, 
  AlertTriangle,
  TrendingUp,
  Layers,
  Activity,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useFieldMonitoring } from '../../hooks/useFieldMonitoring'
import {
  HealthScoreWidget,
  NDVITrendChart,
  StressAlertsPanel,
  ManagementZonesPanel,
  NDVIMapOverlay,
} from './monitoring'
import type { StressEventStatus } from '../../types'

interface FieldMonitoringCardProps {
  fieldId: string
  fieldPolygon?: [number, number][]
  bounds?: {
    northEast: { lat: number; lng: number }
    southWest: { lat: number; lng: number }
  }
  onViewNdviOverlay?: (overlayUrl: string) => void
  onViewZonesOnMap?: (zones: any[], geoJSON?: any) => void
  onStressEventClick?: (eventId: string) => void
}

/**
 * AGC: Collapsible section component
 */
const CollapsibleSection: React.FC<{
  title: string
  icon: React.ReactNode
  badge?: number
  badgeColor?: 'red' | 'amber' | 'blue' | 'green'
  children: React.ReactNode
  defaultOpen?: boolean
}> = ({ title, icon, badge, badgeColor = 'blue', children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const badgeColorClass = useMemo(() => {
    switch (badgeColor) {
      case 'red': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
      case 'amber': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
      case 'green': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
      default: return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
    }
  }, [badgeColor])

  return (
    <div className="border-t border-gray-100 dark:border-gray-700">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">{icon}</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</span>
          {badge !== undefined && badge > 0 && (
            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${badgeColorClass}`}>
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  )
}

export const FieldMonitoringCard: React.FC<FieldMonitoringCardProps> = ({
  fieldId,
  fieldPolygon,
  bounds,
  onViewNdviOverlay,
  onViewZonesOnMap,
  onStressEventClick,
}) => {
  const { t } = useTranslation()
  const [trendDateRange, setTrendDateRange] = useState<'30' | '60' | '90'>('60')
  const [showNdviOverlay, setShowNdviOverlay] = useState(false)

  // AGC: Use field monitoring hook
  const {
    health,
    observations,
    stressEvents,
    zones,
    trend,
    alertSummary,
    ndviOverlay,
    isLoading,
    isRefreshing,
    error,
    lastUpdated,
    refetch,
    fetchTrend,
    fetchZones,
    fetchNdviOverlay,
    updateEventStatus,
  } = useFieldMonitoring({
    fieldId,
    fieldPolygon,
    bounds,
    autoFetch: true,
  })

  // AGC: Handle refresh
  const handleRefresh = useCallback(async () => {
    await refetch({ forceRefresh: true, includeTrend: true })
  }, [refetch])

  // AGC: Handle date range change for trend
  const handleTrendDateRangeChange = useCallback(async (range: '30' | '60' | '90') => {
    setTrendDateRange(range)
    const days = parseInt(range)
    const endDate = new Date().toISOString().split('T')[0]
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    await fetchTrend({ startDate, endDate })
  }, [fetchTrend])

  // AGC: Handle event status change
  const handleEventStatusChange = useCallback(async (eventId: string, status: StressEventStatus) => {
    await updateEventStatus(eventId, status)
  }, [updateEventStatus])

  // AGC: Handle NDVI overlay toggle
  const handleNdviOverlayToggle = useCallback(async (visible: boolean) => {
    setShowNdviOverlay(visible)
    if (visible && !ndviOverlay) {
      await fetchNdviOverlay()
    }
    if (visible && ndviOverlay && onViewNdviOverlay) {
      onViewNdviOverlay(ndviOverlay.url)
    }
  }, [ndviOverlay, fetchNdviOverlay, onViewNdviOverlay])

  // AGC: Handle load zones (force refresh to get fresh data)
  const handleLoadZones = useCallback(async () => {
    console.log('AGC: Loading management zones with force refresh')
    await fetchZones(3, true)
  }, [fetchZones])

  // AGC: Determine badge color for alerts
  const alertBadgeColor = useMemo(() => {
    if (!alertSummary) return 'blue'
    if (alertSummary.bySeverity.critical > 0) return 'red'
    if (alertSummary.bySeverity.warning > 0) return 'amber'
    return 'green'
  }, [alertSummary])

  // AGC: Error state
  if (error && !health && !isLoading) {
    return (
      <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
              <Satellite className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('fields.monitoring.satelliteHealth')}
            </h3>
          </div>
          
          <div className="flex items-center justify-center p-6 bg-red-50 dark:bg-red-900/20 rounded-xl">
            <div className="text-center">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                {error}
              </p>
              <button
                onClick={handleRefresh}
                className="px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4 inline mr-1" />
                {t('common.refresh')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* AGC: Header */}
      <div className="p-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
              <Satellite className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('fields.monitoring.satelliteHealth')}
            </h3>
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* AGC: Health Score Widget - Always Visible */}
        <HealthScoreWidget
          health={health}
          trend={trend?.trend}
          lastUpdated={lastUpdated}
          isLoading={isLoading}
        />
      </div>

      {/* AGC: Collapsible Sections */}
      <div className="px-4">
        {/* AGC: NDVI Trend Chart */}
        <CollapsibleSection
          title={t('fields.monitoring.ndviTrend')}
          icon={<TrendingUp className="w-4 h-4" />}
          defaultOpen={false}
        >
          <NDVITrendChart
            trend={trend}
            observations={observations}
            dateRange={trendDateRange}
            onDateRangeChange={handleTrendDateRangeChange}
            isLoading={isLoading && !trend}
          />
        </CollapsibleSection>

        {/* AGC: Stress Alerts */}
        <CollapsibleSection
          title={t('fields.monitoring.stressAlerts')}
          icon={<AlertTriangle className="w-4 h-4" />}
          badge={alertSummary?.total || 0}
          badgeColor={alertBadgeColor as 'red' | 'amber' | 'green'}
          defaultOpen={alertSummary?.hasActionRequired || false}
        >
          <StressAlertsPanel
            alerts={health?.alerts || []}
            events={stressEvents}
            summary={alertSummary}
            onEventStatusChange={handleEventStatusChange}
            isLoading={isLoading && !health}
          />
        </CollapsibleSection>

        {/* AGC: Management Zones */}
        <CollapsibleSection
          title={t('fields.monitoring.managementZones')}
          icon={<Layers className="w-4 h-4" />}
          defaultOpen={false}
        >
          {zones ? (
            <ManagementZonesPanel
              zones={zones}
              onToggleZoneOverlay={async () => {
                // AGC: Call parent callback with zones data for map rendering
                if (onViewZonesOnMap && zones) {
                  onViewZonesOnMap(zones)
                }
                // AGC: Also fetch and pass NDVI overlay for colored visualization
                if (onViewNdviOverlay) {
                  const overlayUrl = ndviOverlay?.url || await fetchNdviOverlay()
                  if (overlayUrl) {
                    onViewNdviOverlay(overlayUrl)
                  }
                }
              }}
              showOverlay={showNdviOverlay}
              isLoading={false}
            />
          ) : (
            <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <button
                onClick={handleLoadZones}
                className="px-3 py-1.5 text-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
              >
                <Layers className="w-4 h-4 inline mr-1" />
                {t('fields.monitoring.loadZones')}
              </button>
            </div>
          )}
        </CollapsibleSection>

        {/* AGC: NDVI Overlay */}
        <CollapsibleSection
          title={t('fields.monitoring.ndviOverlay')}
          icon={<Activity className="w-4 h-4" />}
          defaultOpen={false}
        >
          <NDVIMapOverlay
            config={ndviOverlay}
            onOpacityChange={(opacity) => {
              // AGC: Could update overlay opacity in state
              console.log('AGC: Overlay opacity changed:', opacity)
            }}
            onRefresh={async () => {
              await fetchNdviOverlay()
            }}
            onToggle={handleNdviOverlayToggle}
            isVisible={showNdviOverlay}
            isLoading={false}
          />
        </CollapsibleSection>
      </div>

      {/* AGC: Footer */}
      <div className="px-4 pb-4 pt-2">
        <p className="text-xs text-center text-gray-400 dark:text-gray-500">
          {t('fields.monitoring.poweredBy')} Sentinel-2 | 10m {t('fields.monitoring.resolution')}
        </p>
      </div>
    </div>
  )
}

export default FieldMonitoringCard

